/**
 * ContentForm Component
 * Content editing form with switchable legacy/multilingual input modes
 * Requirements 1.1, 1.2, 1.3, 1.4, 1.5 implementation
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Form, Input, Switch, Card, Divider, Typography, Alert, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { I18nInput } from './I18nInput';
import { I18nFormFields as _I18nFormFields } from './I18nFormFields'; // Task 8에서 사용 예정
import { ContentFormData, I18nFieldData, InputMode, ContentData } from '../../types/i18n/i18nTypes';
import {
  convertLegacyToI18n,
  convertI18nToLegacy as _convertI18nToLegacy, // Task 7에서 사용 예정
  createEmptyI18nField,
  cloneI18nField,
} from '../../services/i18n/i18nService';
import { I18N_CONFIG } from '../../../config/i18nConfig';
import './ContentForm.css';

const { Text, Title } = Typography;
const { TextArea } = Input;

export interface ContentFormProps {
  /** Content data to edit */
  content?: ContentData;
  /** Data change handler */
  onChange?: (data: ContentFormData) => void;
  /** Initial input mode */
  initialMode?: InputMode;
  /** Form instance (Ant Design Form) */
  form?: any;
  /** Whether to disable the form */
  disabled?: boolean;
  /** Whether to disable mode switching */
  disableModeSwitch?: boolean;
  /** Render function for additional fields */
  renderAdditionalFields?: () => React.ReactNode;
}

/**
 * ContentForm Component
 * Provides a content editing form that can switch between legacy and multilingual fields.
 */
export const ContentForm: React.FC<ContentFormProps> = ({
  content,
  onChange,
  initialMode = 'legacy',
  form,
  disabled = false,
  disableModeSwitch = false,
  renderAdditionalFields,
}) => {
  // Current input mode state
  const [inputMode, setInputMode] = useState<InputMode>(initialMode);

  // Form data state
  const [formData, setFormData] = useState<ContentFormData>(() => {
    if (content) {
      return convertLegacyToI18n(content);
    }
    return {
      title: '',
      title_en: '',
      synopsis: '',
      synopsis_en: '',
      seo_title: '',
      seo_synopsis: '',
      seo_keyword: '',
      i18n_title: createEmptyI18nField(),
      i18n_synopsis: createEmptyI18nField(),
      i18n_seo_title: createEmptyI18nField(),
      i18n_seo_synopsis: createEmptyI18nField(),
      i18n_seo_keyword: createEmptyI18nField(),
    };
  });

  // Sync state tracking
  const [lastSyncMode, setLastSyncMode] = useState<InputMode>(inputMode);

  // Set form data on initial data load
  useEffect(() => {
    if (content) {
      const convertedData = convertLegacyToI18n(content);
      setFormData(convertedData);

      // Ant Design Form에 값 설정
      if (form) {
        form.setFieldsValue({
          title: convertedData.title,
          title_en: convertedData.title_en,
          synopsis: convertedData.synopsis,
          synopsis_en: convertedData.synopsis_en,
          seo_title: convertedData.seo_title,
          seo_synopsis: convertedData.seo_synopsis,
          seo_keyword: convertedData.seo_keyword,
        });
      }
    }
  }, [content, form]);

  // Notify parent component on form data change
  useEffect(() => {
    if (onChange) {
      onChange(formData);
    }
  }, [formData, onChange]);

  // Input mode switch handler
  const handleModeChange = useCallback(
    (checked: boolean) => {
      const newMode: InputMode = checked ? 'i18n' : 'legacy';

      // Sync data on mode switch
      let updatedFormData = { ...formData };

      if (newMode === 'i18n' && lastSyncMode === 'legacy') {
        // Legacy -> i18n mode: Sync legacy field values to i18n fields
        updatedFormData = {
          ...formData,
          i18n_title: {
            ...formData.i18n_title,
            ko: formData.title || formData.i18n_title.ko,
            en: formData.title_en || formData.i18n_title.en,
          },
          i18n_synopsis: {
            ...formData.i18n_synopsis,
            ko: formData.synopsis || formData.i18n_synopsis.ko,
            en: formData.synopsis_en || formData.i18n_synopsis.en,
          },
          i18n_seo_title: {
            ...formData.i18n_seo_title,
            ko: formData.seo_title || formData.i18n_seo_title.ko,
          },
          i18n_seo_synopsis: {
            ...formData.i18n_seo_synopsis,
            ko: formData.seo_synopsis || formData.i18n_seo_synopsis.ko,
          },
          i18n_seo_keyword: {
            ...formData.i18n_seo_keyword,
            ko: formData.seo_keyword || formData.i18n_seo_keyword.ko,
          },
        };
      } else if (newMode === 'legacy' && lastSyncMode === 'i18n') {
        // i18n -> Legacy mode: Sync i18n field values to legacy fields
        updatedFormData = {
          ...formData,
          title: formData.i18n_title.ko || formData.title,
          title_en: formData.i18n_title.en || formData.title_en,
          synopsis: formData.i18n_synopsis.ko || formData.synopsis,
          synopsis_en: formData.i18n_synopsis.en || formData.synopsis_en,
          seo_title: formData.i18n_seo_title.ko || formData.seo_title,
          seo_synopsis: formData.i18n_seo_synopsis.ko || formData.seo_synopsis,
          seo_keyword: formData.i18n_seo_keyword.ko || formData.seo_keyword,
        };

        // Ant Design Form 값 업데이트
        if (form) {
          form.setFieldsValue({
            title: updatedFormData.title,
            title_en: updatedFormData.title_en,
            synopsis: updatedFormData.synopsis,
            synopsis_en: updatedFormData.synopsis_en,
            seo_title: updatedFormData.seo_title,
            seo_synopsis: updatedFormData.seo_synopsis,
            seo_keyword: updatedFormData.seo_keyword,
          });
        }
      }

      setFormData(updatedFormData);
      setInputMode(newMode);
      setLastSyncMode(newMode);
    },
    [formData, lastSyncMode, form]
  );

  // Legacy field change handler
  const handleLegacyFieldChange = useCallback(
    (fieldName: string, value: string) => {
      const updatedFormData = { ...formData, [fieldName]: value };

      // Real-time sync: Legacy fields -> i18n fields
      if (fieldName === 'title') {
        updatedFormData.i18n_title = { ...formData.i18n_title, ko: value };
      } else if (fieldName === 'title_en') {
        updatedFormData.i18n_title = { ...formData.i18n_title, en: value };
      } else if (fieldName === 'synopsis') {
        updatedFormData.i18n_synopsis = { ...formData.i18n_synopsis, ko: value };
      } else if (fieldName === 'synopsis_en') {
        updatedFormData.i18n_synopsis = { ...formData.i18n_synopsis, en: value };
      } else if (fieldName === 'seo_title') {
        updatedFormData.i18n_seo_title = { ...formData.i18n_seo_title, ko: value };
      } else if (fieldName === 'seo_synopsis') {
        updatedFormData.i18n_seo_synopsis = { ...formData.i18n_seo_synopsis, ko: value };
      } else if (fieldName === 'seo_keyword') {
        updatedFormData.i18n_seo_keyword = { ...formData.i18n_seo_keyword, ko: value };
      }

      setFormData(updatedFormData);
    },
    [formData]
  );

  // i18n field change handler
  const handleI18nFieldChange = useCallback(
    (fieldName: string, value: I18nFieldData) => {
      const updatedFormData = {
        ...formData,
        [`i18n_${fieldName}`]: cloneI18nField(value),
      };

      // Real-time sync: i18n fields -> Legacy fields
      if (fieldName === 'title') {
        updatedFormData.title = value.ko || formData.title;
        updatedFormData.title_en = value.en || formData.title_en;
      } else if (fieldName === 'synopsis') {
        updatedFormData.synopsis = value.ko || formData.synopsis;
        updatedFormData.synopsis_en = value.en || formData.synopsis_en;
      } else if (fieldName === 'seo_title') {
        updatedFormData.seo_title = value.ko || formData.seo_title;
      } else if (fieldName === 'seo_synopsis') {
        updatedFormData.seo_synopsis = value.ko || formData.seo_synopsis;
      } else if (fieldName === 'seo_keyword') {
        updatedFormData.seo_keyword = value.ko || formData.seo_keyword;
      }

      // Ant Design Form 값 업데이트 (레거시 모드에서도 동기화)
      if (form) {
        const formValues: any = {};
        if (fieldName === 'title') {
          formValues.title = updatedFormData.title;
          formValues.title_en = updatedFormData.title_en;
        } else if (fieldName === 'synopsis') {
          formValues.synopsis = updatedFormData.synopsis;
          formValues.synopsis_en = updatedFormData.synopsis_en;
        } else if (fieldName === 'seo_title') {
          formValues.seo_title = updatedFormData.seo_title;
        } else if (fieldName === 'seo_synopsis') {
          formValues.seo_synopsis = updatedFormData.seo_synopsis;
        } else if (fieldName === 'seo_keyword') {
          formValues.seo_keyword = updatedFormData.seo_keyword;
        }
        form.setFieldsValue(formValues);
      }

      setFormData(updatedFormData);
    },
    [formData, form]
  );

  // Mode description text
  const modeDescription = useMemo(() => {
    if (inputMode === 'legacy') {
      return {
        title: 'Legacy Input Mode',
        description: 'Enter Korean and English fields individually using the existing format.',
        color: 'blue',
      };
    } else {
      return {
        title: 'Multilingual Input Mode',
        description: 'Manage multiple languages efficiently with a tab-based interface.',
        color: 'green',
      };
    }
  }, [inputMode]);

  // Check if i18n feature is enabled
  const isI18nEnabled = I18N_CONFIG.enableI18nFields;

  return (
    <div className="content-form-wrapper">
      {/* Input mode switch section */}
      {isI18nEnabled && (
        <Card className="content-form-mode-card" size="small">
          <div className="content-form-mode-header">
            <div className="content-form-mode-info">
              <Title level={5} className="content-form-mode-title">
                {modeDescription.title}
              </Title>
              <Text type="secondary" className="content-form-mode-description">
                {modeDescription.description}
              </Text>
            </div>
            <div className="content-form-mode-switch">
              <Space>
                <Text>Legacy</Text>
                <Switch
                  checked={inputMode === 'i18n'}
                  onChange={handleModeChange}
                  disabled={disabled || disableModeSwitch}
                  checkedChildren="i18n"
                  unCheckedChildren="Legacy"
                />
                <Text>i18n</Text>
              </Space>
            </div>
          </div>

          {/* Mode-specific info message */}
          <Alert
            message={
              <Space>
                <InfoCircleOutlined />
                <span>
                  {inputMode === 'legacy'
                    ? 'Legacy mode uses existing fields (title, title_en, etc.). Your input is automatically synced with multilingual fields.'
                    : 'Multilingual mode allows you to manage multiple languages via tabs. Korean is required; other languages are optional.'}
                </span>
              </Space>
            }
            type="info"
            showIcon={false}
            className="content-form-mode-alert"
          />
        </Card>
      )}

      {/* Basic information section */}
      <Card title="Basic Information" className="content-form-section">
        {inputMode === 'legacy' ? (
          // Legacy input mode
          <>
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: 'Please enter a title!' }]}
            >
              <Input
                placeholder="Enter Korean title"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('title', e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Title (English)" name="title_en">
              <Input
                placeholder="Enter English title"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('title_en', e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Synopsis" name="synopsis">
              <TextArea
                rows={4}
                placeholder="Enter Korean synopsis"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('synopsis', e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Synopsis (English)" name="synopsis_en">
              <TextArea
                rows={4}
                placeholder="Enter English synopsis"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('synopsis_en', e.target.value)}
              />
            </Form.Item>
          </>
        ) : (
          // Multilingual input mode
          <>
            <I18nInput
              label="Title"
              value={formData.i18n_title}
              onChange={value => handleI18nFieldChange('title', value)}
              required={true}
              fieldName="title"
              disabled={disabled}
            />

            <Divider />

            <I18nInput
              label="Synopsis"
              value={formData.i18n_synopsis}
              onChange={value => handleI18nFieldChange('synopsis', value)}
              multiline={true}
              rows={4}
              fieldName="synopsis"
              disabled={disabled}
            />
          </>
        )}
      </Card>

      {/* SEO information section */}
      <Card title="SEO Information" className="content-form-section">
        {inputMode === 'legacy' ? (
          // Legacy SEO fields
          <>
            <Form.Item label="SEO Title" name="seo_title">
              <Input
                placeholder="Enter SEO title"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('seo_title', e.target.value)}
              />
            </Form.Item>

            <Form.Item label="SEO Description" name="seo_synopsis">
              <TextArea
                rows={3}
                placeholder="Enter SEO description"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('seo_synopsis', e.target.value)}
              />
            </Form.Item>

            <Form.Item label="SEO Keyword" name="seo_keyword">
              <Input
                placeholder="Enter SEO keyword (comma separated)"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('seo_keyword', e.target.value)}
              />
            </Form.Item>
          </>
        ) : (
          // Multilingual SEO fields
          <>
            <I18nInput
              label="SEO Title"
              value={formData.i18n_seo_title}
              onChange={value => handleI18nFieldChange('seo_title', value)}
              fieldName="seo_title"
              disabled={disabled}
            />

            <Divider />

            <I18nInput
              label="SEO Description"
              value={formData.i18n_seo_synopsis}
              onChange={value => handleI18nFieldChange('seo_synopsis', value)}
              multiline={true}
              rows={3}
              fieldName="seo_synopsis"
              disabled={disabled}
            />

            <Divider />

            <I18nInput
              label="SEO Keyword"
              value={formData.i18n_seo_keyword}
              onChange={value => handleI18nFieldChange('seo_keyword', value)}
              fieldName="seo_keyword"
              disabled={disabled}
              helperText="Enter keywords separated by commas"
            />
          </>
        )}
      </Card>

      {/* Render additional fields */}
      {renderAdditionalFields && renderAdditionalFields()}
    </div>
  );
};

export default ContentForm;
